import { Component, OnDestroy, OnInit, Input, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { sortLetters } from '@app/_utils/converters';

import { TaskService } from '@app/core/services/task.service';
import { PupilService } from '@student/services/pupil.service';
import { StudentClass, Lesson, Pupil, Group, Profile } from '@df/interfaces';
import { MAIN_DOMAIN, GROUPS } from '@app/app.constants';

import { CarouselConfig } from 'ngx-bootstrap/carousel';
import { MatDialog } from '@angular/material/dialog';
import { AUTH_TOKEN } from '@app/_interceptors/auth.interceptor';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AuthService } from '@app/_modules/auth/services/auth.service';
import { HandlerService } from '@app/_services/handler.service';
import { AccountService } from '@app/teacher/services/account.service';

@Component({
    selector: 'app-pupil-choice',
    templateUrl: './pupil-choice.component.html',
    styleUrls: ['./pupil-choice.component.scss'],
    providers: [
        {
            provide: CarouselConfig,
            useValue: {
                interval: 0,
                showIndicators: false,
                activeSlideIndex: 0
            }
        }
    ]
})
export class PupilChoiceComponent implements OnInit, OnDestroy {
    @Input() coach: boolean = false;
    @Input() choicedPupil: Pupil;

    @ViewChild('confirmCoach', { static: true }) confirmCoach: TemplateRef<any>;
    @ViewChild('checkUser', { static: true }) checkUser: TemplateRef<any>;

    classInstance: StudentClass;
    letter: string;
    letters: Array<string> = [];
    currentStudents: any[] = [];
    MAIN_DOMAIN = MAIN_DOMAIN;
    lesson: Lesson;
    dialogRef: any;
    coachPupil: Pupil;
    formCheckUser: FormGroup;
    hide: boolean = true;
    profile: Profile;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private route: ActivatedRoute,
        private authService: AuthService,
        private accountService: AccountService,
        private handlerMsg: HandlerService,
        private router: Router,
        public dialog: MatDialog,
        private pupilService: PupilService,
        private taskService: TaskService,
        private fb: FormBuilder
    ) {}

    ngOnInit(): void {
        this.formCheckUser = this.fb.group({
            password: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(30)]]
        });

        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const class_id: number = params['class_id'];
            if (!!class_id) {
                const classInstance = this.pupilService.getCachedClass(class_id);
                if (classInstance) {
                    this.classInstance = classInstance;
                    this.getCurrentStudents();
                } else {
                    this.pupilService
                        .getClass(class_id)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(classResp => {
                            this.classInstance = classResp;
                            this.letters = sortLetters(classResp.students);
                            this.pupilService.setCachedClass(classResp);
                            this.getCurrentStudents();
                        });
                }
            }
        });
    }

    private checkLesson(): void {
        this.taskService
            .getCurrentPupil(this.choicedPupil.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                resp => {
                    if (resp) {
                        this.lesson = resp;
                        if (this.lesson.is_habirint && !this.coach) {
                            this.router.navigate(['student/coach', this.classInstance.id]);
                            return;
                        }
                        if (this.lesson.smart_sprints.length) {
                            this.router.navigate(['student/smart_sprint/pupil', this.choicedPupil.id, this.lesson.id]);
                            return;
                        }
                        if (
                            (this.lesson.is_online && !localStorage.getItem(AUTH_TOKEN)) ||
                            (this.lesson.is_online &&
                                this.choicedPupil.user.id !== +localStorage.getItem('CURRENT_USER_ID'))
                        ) {
                            this.dialogRef = this.dialog.open(this.checkUser, {
                                width: '527px',
                                height: '250px'
                            });
                            return;
                        }

                        this.router.navigate(['/student/pupil', this.choicedPupil.id]);
                    }
                },
                // @ts-ignore-start: параметор "error" небыл прочитан
                error => {
                    // @ts-ignore-end
                    this.router.navigate([`/student/pupil/${this.choicedPupil.name}/lesson_not_started`]);
                }
            );
    }

    onExit() {
        if (!this.letter) {
            this.router.navigate(['/student/class']);
        }
        this.letter = '';
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    onChoice(id: number): void {
        const currentPupil = this.pupilService.getStudentFromClass(id);
        if (this.coach) {
            this.pupilService.setCoachPupil = currentPupil;
            this.coachPupil = currentPupil;
            this.openDialog();
            return;
        }
        if (!this.coach) {
            this.choicedPupil = currentPupil;
            this.pupilService.setCurrentPupil(this.choicedPupil);
        }
        this.checkLesson();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    getCurrentStudents(): void {
        this.currentStudents = [];
        const students = this.classInstance.students.slice();

        for (const student of students) {
            const first_letter = student.short_name ? student.short_name.charAt(0) : student.name.charAt(0);
            if (first_letter === this.letter) {
                this.currentStudents.push(student);
            }
        }
    }

    choiceLetter(letter: string): void {
        this.letter = letter;
        this.getCurrentStudents();
    }

    openDialog(): void {
        this.dialogRef = this.dialog.open(this.confirmCoach, {
            width: '60%',
            height: '430px'
        });
    }

    confirmChosedCoach(): void {
        this.checkLesson();
        this.closeDialog();
    }

    abortChosedCoach(): void {
        this.closeDialog();
    }

    checkUserForm(): void {
        const password = this.formCheckUser.value.password;
        const formData = {
            username: this.choicedPupil.user.username,
            password: password
        };

        this.closeDialog();

        this.accountService.getUserGroups(formData.username).subscribe(resp => {
            if (resp.errors.length) {
                const errorMsg = resp.errors[0];
                this.handlerMsg.errorMsg(errorMsg);
                return;
            }

            if (!resp.errors.length && resp.data) {
                this.setStudentGroupId(resp.data);
                this.login(formData);
            }
        });
    }

    private setStudentGroupId(data: Array<Group>) {
        if (data.length === 1) {
            this.accountService.setCurrentGroupId(data[0].id);
            return;
        }
        data.forEach(item => {
            if (item.name === GROUPS.STUDENT) {
                this.accountService.setCurrentGroupId(item.id);
            }
        });
    }

    private login(formData: any): void {
        this.authService.login(formData).subscribe(
            response => {
                if (response && !!response.token) {
                    localStorage.setItem(AUTH_TOKEN, response.token);
                    localStorage.setItem('CURRENT_USER_ID', String(this.choicedPupil.user.id));
                    const url = window.location.href;
                    this.router
                        .navigateByUrl(url, { skipLocationChange: true })
                        .then(() => this.router.navigate(['/student/pupil', this.choicedPupil.id]));
                }
            },
            // @ts-ignore-start: параметор "error" небыл прочитан
            error => {
                // @ts-ignore-end
                const errorMsg = 'Логин и пароль не совпадают';
                this.handlerMsg.errorMsg(errorMsg);
            }
        );
    }
}
